import { MouseEventHandler, RefObject } from 'react';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionProps, AccordionSummary, alpha, Button, Typography } from '@mui/material';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { TransitionProps } from '@mui/material/transitions';

type FilterAccordionProps = {
  label: string;
  count?: number;
  showClear?: boolean;
  focusElementOnExpand?: RefObject<HTMLInputElement | null>; // ref to the element that we want to get focus after accordion expands
  onClear?: MouseEventHandler<HTMLButtonElement>;
} & AccordionProps;

const FilterAccordion = ({ children, label, count = 0, showClear, focusElementOnExpand, onClear, ...restProps }: FilterAccordionProps) => {
  const handleClear: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onClear?.(e);
  };

  const handleExpandTransitionEnd: TransitionProps['onEntered'] = (...args) => {
    (restProps.slotProps?.transition as TransitionProps)?.onEntered?.(...args);

    if (focusElementOnExpand?.current) {
      focusElementOnExpand.current.focus();
    }
  };

  return (
    <StyledAccordion
      {...{
        ...restProps,
        slotProps: {
          ...restProps.slotProps,
          transition: {
            ...restProps.slotProps?.transition,
            onEntered: handleExpandTransitionEnd
          } as TransitionProps
        }
      }}
      disableGutters
    >
      <StyledAccordionSummary expandIcon={<PolarisIcon icon="down_filled" height={8} />}>
        <AccordionTitle>
          {label}
          {count > 0 && <Badge>{count}</Badge>}
        </AccordionTitle>

        {!!showClear && onClear && (
          <ClearButton variant="text" onClick={handleClear}>
            Clear
          </ClearButton>
        )}
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default FilterAccordion;

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: 'none',
  borderRadius: 0,
  border: 'none',
  '&:first-of-type': {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0
  },
  '&:last-of-type': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  '&.MuiAccordion-root.MuiPaper-elevation': {
    boxShadow: 'none'
  },
  '&.MuiAccordion-root .MuiAccordionDetails-root': {
    padding: theme.spacing(1, 2, 2)
  }
}));

const AccordionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.pxToRem(16)
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    flexDirection: 'row',
    padding: theme.spacing(2),
    gap: theme.spacing(1),

    '&:hover': {
      '& .MuiAccordionSummary-content .MuiTypography-root': {
        color: theme.palette.primary.main,
        '& span': {
          color: theme.palette.text.primary
        }
      }
    },

    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      justifyContent: 'space-between',
      '> p': {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(16)
      }
    },

    '&.Mui-expanded': {
      paddingBottom: theme.spacing(1),
      '& span': {
        display: 'none'
      }
    },

    '& .MuiAccordionSummary-expandIconWrapper': {
      color: theme.palette?.filters?.accordion?.icon || theme.palette.text.secondary,
      '&.Mui-expanded': {
        transform: 'rotate(180deg)'
      }
    }
  }
}));

const Badge = styled.span(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: '0.75rem',
  paddingLeft: 'auto',
  borderRadius: 12,
  height: 'fit-content',
  width: 'fit-content',
  padding: theme.spacing(0.25, 1.5),
  lineHeight: 2,
  marginLeft: theme.spacing(1)
}));

const ClearButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 600,
  color: theme.palette?.filters?.clear || theme.palette.text.secondary,
  minWidth: 0,
  padding: theme.spacing(0.25, 0.75),
  '&:hover': {
    backgroundColor: alpha(theme.palette?.filters?.clear || theme.palette.primary.main, 0.08)
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  '&.MuiAccordionDetails-root': {
    padding: theme.spacing(1, 2, 2)
  }
}));
